import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// An author playing a lesson from Teach (see `LessonSession#preview` in the Rails app). While `lessonId` is set,
// the loaders of that lesson read the preview endpoints, which serve unpublished content and keep the run out of
// the author's own progress. Kept per tab, so a reload stays in the preview.
export const LESSON_PREVIEW_STORAGE_KEY = 'lingu.lessonPreview';

export interface ILessonPreviewState {
  lessonId: number | null;
}

export function readStoredPreviewLessonId(): number | null {
  try {
    const lessonId = Number(window.sessionStorage.getItem(LESSON_PREVIEW_STORAGE_KEY));
    return Number.isInteger(lessonId) && lessonId > 0 ? lessonId : null;
  } catch {
    return null;
  }
}

export function storePreviewLessonId(lessonId: number | null): void {
  try {
    if (lessonId === null) {
      window.sessionStorage.removeItem(LESSON_PREVIEW_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(LESSON_PREVIEW_STORAGE_KEY, String(lessonId));
    }
  } catch {
    // No session storage (some webviews): the preview lasts until the next reload.
  }
}

interface IWithLessonPreview {
  shared: { lesson: { preview?: ILessonPreviewState } };
}

/** Whether the loaders of `lessonId` read the preview endpoints. */
export function isPreviewingLesson(state: IWithLessonPreview, lessonId: number): boolean {
  return state.shared.lesson.preview?.lessonId === lessonId;
}

const lessonPreviewSlice = createSlice({
  name: 'lessonPreview',
  initialState: (): ILessonPreviewState => ({ lessonId: readStoredPreviewLessonId() }),
  reducers: {
    enter(_, action: PayloadAction<number>) {
      return { lessonId: action.payload };
    },
    exit() {
      return { lessonId: null };
    }
  }
});

export const lessonPreviewActions = lessonPreviewSlice.actions;
export default lessonPreviewSlice.reducer;
